import { axios } from "../http/axios.js";
import { api } from '../../constants/api.js';
import { notification } from "antd";

const ecommerceService = {
    //Commerce Products - Create/Update/Delete
    getProductFromUrl: async (url) => {
        try {
            const result = await axios.post(api.products.scrapeProduct, { url });
            return result.data;
        } catch (error) {
            notification.error({
                message: error.response?.data?.message || "Failed to scrape product",
            });
            throw error;
        }
    },    
    getCommerceProducts: async (storeName) => {
        try {
            const result = await axios.get(api.store.fetchCommerceProducts(storeName));
            return result.data;
        } catch (error) {
            notification.error({
                message: error.response?.data?.message || "Failed to fetch products",
            });
            throw error;
        }
    },

    getCommerceProductsPaginated: async (storeName, page = 1, limit = 10) => {
        try {
            const result = await axios.get(api.store.fetchCommerceProducts(storeName), {
                params: { page, limit }
            });
            return result.data;
        } catch (error) {
            notification.error({
                message: error.response?.data?.message || "Failed to fetch products",
            });
            throw error;
        }
    },

    createCommerceProduct: async (productData) => {
        try {
            const result = await axios.post(api.products.createCommerceProduct, productData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            return result.data;
        } catch (error) {
            notification.error({
                message: error.response?.data?.message || "createCommerceProduct: Something went wrong",
            });
            throw error;
        }
    },

    fetchCommerceProduct: async (productId) => {
        try {
            const result = await axios.get(api.products.fetchCommerceProduct.replace(":id", productId));
            return result.data;
        } catch (error) {
            notification.error({
                message: error.response?.data?.message || "fetchCommerceProduct: Something went wrong",
            });
            throw error;
        }
    },

    updateCommerceProduct: async (productId, productData) => {
        try {
            const url = api.products.updateCommerceProduct.replace(":id", productId);
            const result = await axios.post(url, productData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            return result.data;
        } catch (error) {
            notification.error({
                message: error.response?.data?.message || "updateCommerceProduct: Something went wrong",
            });
            throw error;
        }
    },

    fetchShopSettingsandMetrics: async () => {
        try {
            const result = await axios.get(api.products.fetchShopSettingsandMetrics);
            return result.data;
        } catch (error) {
            notification.error({
                message: error.response?.data?.message || "fetchShopSettingsandMetrics: Something went wrong",
            });
            throw error;
        }
    },

    updateShopSettings: async (settings) => {
        try {
            const response = await axios.put(api.products.updateShopSettings, settings);
            return response.data;
        } catch (error) {
            notification.error({
                message: error.response?.data?.message || "updateShopSettings: Something went wrong",
            });
            throw error;
        }
    },

    updateCommerceProductPublishStatus: async (productId, isPublished, type) => {
        try {
            const result = await axios.put(api.products.updatePublishStatus(productId), { isPublished, type });
            return result.data;
        } catch (error) {
            notification.error({
                message: error.response?.data?.message || "Failed to update product publish status",
            });
            throw error;
        }
    },
    //Shop.sparkle.store dashboard api
    fetchCollections: async (category) => {
        try {
            const response = await axios.get(api.ecommerceShop.fetchCollections(category));
            return response.data;
        } catch (error) {
            console.error('Error fetching collections:', error);
            throw error; // Re-throw the error so it can be handled by the component
        }
    },

    fetchCategories: async () => {
        try {
            const response = await axios.get(api.ecommerceShop.fetchCategories);
            return response.data;
        } catch (error) {
            console.error('Error fetching collections:', error);
            throw error; // Re-throw the error so it can be handled by the component
        }
    },

    fetchProductsForCollection: async (category, collection, params = {}) => {
        try {
            const response = await axios.get(api.ecommerceShop.fetchProductsForCollection(category, collection), {
                params: params
            });
            return response.data;
        } catch (error) {
            console.error('Error fetching products for collection:', error);
            throw error;
        }
    },

    fetchProductsForCustomCollection: async (category, collection, params = {}) => {
        try {
            const response = await axios.get(api.ecommerceShop.fetchProductsForCustomCollection(category, collection), {
                params: params
            });
            return response.data;
        } catch (error) {
            console.error('Error fetching products for custom collection:', error);
            throw error;
        }
    },

    // New method for fetching products by category (including tag-based filtering)
    getProductsByCategory: async (category, params = {}) => {
        try {
            const response = await axios.get(api.ecommerceShop.fetchProductsByCategory(category), {
                params: params
            });
            return response.data;
        } catch (error) {
            console.error('Error fetching products by category:', error);
            throw error;
        }
    },

    // Method for fetching collections by category  
    getCollectionsByCategory: async (category, params = {}) => {
        try {
            const response = await axios.get(api.ecommerceShop.fetchCollections(category), {
                params: params
            });
            return response.data;
        } catch (error) {
            console.error('Error fetching collections by category:', error);
            throw error;
        }
    },    
    fetchProduct: async (category, collection, productSlug, storeName) => {
        const response = await axios.get(api.ecommerceShop.fetchProduct(category, collection, productSlug), {
            params: { storeName }
        });
        return response.data;
    },

    // New method to handle both direct products and collection-based products    
    getProductBySlug: async (category, collectionSlug, productSlug) => {
        try {            
            // Build the API URL based on whether collectionSlug is "product" (indicating direct product route)
            let endpoint;
            if (collectionSlug === 'product') {
                // Direct product URL: /shop/:category/product/:productSlug
                endpoint = api.ecommerceShop.fetchDirectProduct(category, productSlug);
            } else {
                // Traditional collection-based URL: /shop/:category/:collection/:productSlug
                endpoint = api.ecommerceShop.fetchProduct(category, collectionSlug, productSlug);
            }

            const response = await axios.get(endpoint);
            return response.data;
        } catch (error) {
            console.error('Error fetching product by slug:', error);
            throw error;
        }
    },

    fetchProductByCreator: async (storeName, productSlug) => {
        const response = await axios.get(api.ecommerceShop.fetchProductByCreator(storeName, productSlug));
        return response.data;
    },
    addProductToCreatorStore: async (formData) => {
        try {
            const response = await axios.post(api.ecommerceShop.addProductToCreatorStore, formData);

            return response.data;
        } catch (error) {
            console.error('Error adding product to creator store:', error);
            throw error;
        }
    },

    fetchOrderDetails: async (sessionId) => {
        try {
            const response = await axios.get(api.ecommerceShop.fetchOrderDetails(sessionId));
            return response.data;
        } catch (error) {
            console.error('Error fetching order details:', error);
            throw error;
        }
    },

    fetchCustom3DProduct: async (productSlug, username) => {
        try {
            // Use the public endpoint if username is undefined, null, or an empty string
            const endpoint = username && username.trim() !== '' ?
                api.digitalProducts.fetchCustom3DProduct(productSlug) :
                api.digitalProducts.fetchPublicCustom3DProduct(productSlug);

            const response = await axios.get(
                endpoint,
                { params: { username } }
            );
            return response.data;
        } catch (error) {
            notification.error({
                message: error.response?.data?.message || "Failed to fetch custom 3D product",
            });
            throw error;
        }
    },

    // Decomissioned 
    generateFinalImage: async (payload) => {
        try {
            const response = await axios.post(
                api.digitalProducts.generateFinalImage,
                payload
            );
            return response.data;
        } catch (error) {
            notification.error({
                message: error.response?.data?.message || "Failed to generate final image",
            });
            throw error;
        }
    },

    getPublicProducts: async (params = {}) => {
        try {
            const response = await axios.get(api.ecommerceShop.fetchPublicProducts, { params });
            return response.data;
        } catch (error) {
            console.error('Error fetching public products:', error);
            throw error; // Re-throw the error so it can be handled by the component
        }
    },

    // Regular ecommerce methods (not Google Merchant specific)
    getEcommerceProducts: async (params = { limit: 50 }) => {
        try {
            const result = await axios.get(api.shop.fetchProducts, { params });
            return result.data;
        } catch (error) {
            notification.error({
                message: error.response?.data?.message || "Failed to fetch ecommerce products",
            });
            throw error;
        }
    },

    updateProduct: async (productData) => {
        try {
            // If it's a full product object with _id, use that as the ID
            const productId = productData.id || productData._id;

            if (!productId) {
                throw new Error('Product ID is required for updating a product');
            }

            // Create a new FormData object for the multipart/form-data request
            const formData = new FormData();

            // Add basic product data
            formData.append('title', productData.name || productData.title);
            formData.append('price', productData.price);

            if (productData.description) {
                formData.append('description', productData.description);
            }

            if (productData.category) {
                formData.append('category', productData.category);
            }

            // Add Google Merchant specific data if available
            if (productData.googleMerchant) {
                formData.append('googleMerchant', JSON.stringify(productData.googleMerchant));
            }

            // Make the API call to update the product
            const result = await axios.put(api.shop.updateProduct(productId), formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });

            return { success: true, product: result.data };
        } catch (error) {
            notification.error({
                message: error.response?.data?.message || "Failed to update product",
            });
            throw error;
        }
    }
};

export default ecommerceService;